import React, { useState, useCallback, useRef } from 'react';
import { Modal } from './Modal.js';
import Button from './Button.js';

/**
 * Promise-based confirm dialog built on the shared ui/ Modal — a drop-in,
 * non-blocking replacement for window.confirm().
 *
 * Usage:
 *   const [confirm, confirmModal] = useConfirm();
 *   // render {confirmModal} once in the component tree
 *   if (await confirm({ title, message, confirmText, danger: true })) { ...do it... }
 *
 * Returns true when the user confirms, false on cancel / backdrop / Escape.
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmText: opts.confirmText || 'Confirm',
        cancelText: opts.cancelText || 'Cancel',
        danger: opts.danger ?? false,
      });
    });
  }, []);

  const close = useCallback((result) => {
    setState(null);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  }, []);

  const confirmModal = state ? (
    <Modal
      isOpen
      onClose={() => close(false)}
      title={state.title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>{state.cancelText}</Button>
          <Button variant={state.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
            {state.confirmText}
          </Button>
        </>
      }
    >
      {state.message && <p className="text-sm text-gray-600">{state.message}</p>}
    </Modal>
  ) : null;

  return [confirm, confirmModal];
}

export default useConfirm;
